// src/pages/FaceEnroll.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import deviceFetch from "../utils/deviceFetch";
import normalize, { fuzzyIncludes } from "../utils/normalize";

const FaceEnroll = () => {
  const { deviceId } = useParams();

  const nav = useNavigate();

  const deviceTokenKey = `turnstileDeviceToken:${deviceId || "default"}`;

  const [deviceToken, setDeviceToken] = useState(() => {
    try {
      return localStorage.getItem(deviceTokenKey) || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    try {
      const t = localStorage.getItem(deviceTokenKey) || "";
      setDeviceToken(t);
    } catch {
      setDeviceToken("");
    }
  }, [deviceTokenKey]);

  useEffect(() => {
    if (!deviceToken) {
      nav("/turnstile-login", { replace: true });
    }
  }, [deviceToken, nav]);

  const [device, setDevice] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [search, setSearch] = useState("");

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [isCameraReady, setIsCameraReady] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);

  const [capturedPhotoUrl, setCapturedPhotoUrl] = useState(null);
  const capturedPhotoUrlRef = useRef(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [statusMessage, setStatusMessage] = useState(null);
  const [statusType, setStatusType] = useState("info"); // info | success | error
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Утилита для статусов
  const showStatus = useCallback((message, type = "info", timeout = 3000) => {
    setStatusMessage(message);
    setStatusType(type);

    if (timeout) {
      setTimeout(() => {
        setStatusMessage(null);
      }, timeout);
    }
  }, []);

  // Загрузка сотрудников (DEVICE TOKEN)
  useEffect(() => {
    if (!deviceId || !deviceToken) return;

    const fetchData = async () => {
      try {
        const res = await deviceFetch(
          `/api/turnstile/devices/${deviceId}/employees?onlyNotMarkedToday=false`,
          { cache: "no-store", deviceToken }
        );

        if (!res.ok) {
          throw new Error("Failed to load device employees");
        }

        const data = await res.json();
        const list = data.users || [];

        setDevice(data.device || null);
        setEmployees(list);
        setFilteredEmployees(list);
      } catch (err) {
        console.error("FaceEnroll: fetchData error:", err);
        showStatus("Не удалось загрузить список сотрудников устройства", "error", 5000);
      }
    };

    fetchData();
  }, [deviceId, deviceToken, showStatus]);

  // Фильтрация по ФИО (с транслитерацией)
  useEffect(() => {
    if (!search?.trim()) {
      setFilteredEmployees(employees.filter((e) => !e.has_face));
      return;
    }

    setFilteredEmployees(
      employees
        .filter((emp) => !emp.has_face)
        .filter((emp) => {
          const fullName = normalize(
            emp.full_name ||
              `${emp.last_name || ""} ${emp.first_name || ""}` ||
              ""
          );
          return fuzzyIncludes(fullName, search);
        })
    );
  }, [search, employees]);

  // Старт камеры
  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      showStatus("Камера не поддерживается этим устройством", "error", 5000);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      streamRef.current = stream;
      setIsCameraReady(true);
      setCameraOn(true);
    } catch (err) {
      console.error("FaceEnroll camera error:", err);
      showStatus(
        "Не удалось открыть камеру (проверьте разрешения)",
        "error",
        5000
      );
      setIsCameraReady(false);
      setCameraOn(false);
    }
  }, [showStatus]);

  // Стоп камеры
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setIsCameraReady(false);
    setCameraOn(false);
    setCapturedPhotoUrl(null);
    capturedPhotoUrlRef.current = null;
  }, []);

  // Тоггл
  const handleToggleCamera = async () => {
    if (cameraOn) {
      stopCamera();
    } else {
      await startCamera();
    }
  };

  // Сфоткать лицо
  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) {
      showStatus("Камера ещё не готова", "error");
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, width, height);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
    setCapturedPhotoUrl(dataUrl);
    capturedPhotoUrlRef.current = dataUrl;
  };

  // Выбор сотрудника
  const handleEmployeeClick = (emp) => {
    setSelectedEmployee(emp);
    setCapturedPhotoUrl(null);
    capturedPhotoUrlRef.current = null;
  };

  // Сохранение лица сотрудника
  const handleSaveFace = async () => {
    if (!selectedEmployee) {
      showStatus("Выберите сотрудника", "error", 4000);
      return;
    }
    
    if (!deviceToken) {
      showStatus("Сначала выполните вход в турникет", "error", 4000);
      return;
    }

    if (!capturedPhotoUrlRef.current) {
      showStatus("Сначала сделайте фото лица", "error", 4000);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await deviceFetch("/api/turnstile/face-enroll", {
        method: "POST",
        deviceToken,
        body: {
          employee_id: selectedEmployee.id,
          image: capturedPhotoUrlRef.current,
        },
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Face enroll failed");
      }

      showStatus(
        `Лицо сотрудника «${
          selectedEmployee.full_name ||
          `${selectedEmployee.last_name || ""} ${
            selectedEmployee.first_name || ""
          }`.trim()
        }» успешно зарегистрировано`,
        "success",
        4000
      );

      // убираем сотрудника из списка (как в ручной отметке турникета)
      setEmployees((prev) => prev.filter((u) => u.id !== selectedEmployee.id));
      setSelectedEmployee(null);
      setCapturedPhotoUrl(null);
      capturedPhotoUrlRef.current = null;

    } catch (err) {
      console.error("FaceEnroll: handleSaveFace error:", err);
      showStatus("Не удалось сохранить лицо сотрудника", "error", 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Чистим камеру при размонтировании
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  return (
    // <Layout>
      <div className="container">
        <div className="face-enroll-page">
          <header className="face-enroll-header">
            <div className="face-enroll-header__title-block">
              <h1 className="face-enroll-header__title">
                Регистрация лица сотрудника
              </h1>
              {device && (
                <div className="face-enroll-header__meta">
                  <span className="face-enroll-header__branch">
                    Филиал: {device.branch || "—"}
                  </span>
                  <span className="face-enroll-header__device-id">
                    Устройство #{device.id}
                  </span>
                </div>
              )}
            </div>

            {/* <div className="face-enroll-header__actions">
              <button
                type="button"
                className="face-enroll-button face-enroll-button--ghost"
                onClick={handleToggleCamera}
              >
                {cameraOn ? "Выключить камеру" : "Включить камеру"}
              </button>
            </div> */}
          </header>

          {statusMessage && (
            <div
              className={`face-enroll-status face-enroll-status--${statusType}`}
            >
              {statusMessage}
            </div>
          )}

          <main className="face-enroll-body">
            <section className="face-enroll-column face-enroll-column--left">
              <div className="face-enroll-search">
                <input
                  type="text"
                  className="face-enroll-search__input"
                  placeholder="Поиск по имени сотрудника..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="face-enroll-employees-list">
                {filteredEmployees.map((emp) => {
                  const fullName =
                    emp.full_name ||
                    `${emp.last_name || ""} ${emp.first_name || ""}`.trim();

                  const isSelected = selectedEmployee?.id === emp.id;

                  return (
                    <button
                      key={emp.id}
                      className={`face-enroll-employee ${
                        isSelected ? "face-enroll-employee--selected" : ""
                      }`}
                      onClick={() => handleEmployeeClick(emp)}
                    >
                      <span className="face-enroll-employee__name">
                        {fullName}
                      </span>
                      {emp.position && (
                        <span className="face-enroll-employee__position">
                          {emp.position}
                        </span>
                      )}
                    </button>
                  );
                })}

                {!filteredEmployees.length && (
                  <div className="face-enroll-placeholder">
                    Нет сотрудников по фильтру
                  </div>
                )}
              </div>
            </section>

            <section className="face-enroll-column face-enroll-column--right">
              <div className="face-enroll-camera">
                <div className="face-enroll-camera__header">
                  <h2 className="face-enroll-camera__title">
                    {selectedEmployee
                      ? `Сотрудник: ${
                          selectedEmployee.full_name ||
                          `${selectedEmployee.last_name || ""} ${
                            selectedEmployee.first_name || ""
                          }`.trim()
                        }`
                      : "Выберите сотрудника слева"}
                  </h2>
                </div>

                <div className="face-enroll-camera__content">
                  <div className="face-enroll-camera__preview">
                    <div className="face-enroll-camera__video-wrapper">
                      <video
                        ref={videoRef}
                        className="face-enroll-camera__video"
                        autoPlay
                        playsInline
                        muted
                      />

                      {!isCameraReady && (
                        <div className="face-enroll-camera__overlay">
                          Камера готовится...
                        </div>
                      )}

                      {capturedPhotoUrl && (
                        <img
                          src={capturedPhotoUrl}
                          alt="Снимок лица"
                          className="face-enroll-camera__image face-enroll-camera__image--overlay"
                        />
                      )}
                    </div>

                    <canvas
                      ref={canvasRef}
                      className="face-enroll-camera__canvas"
                      style={{ display: "none" }}
                    />
                  </div>

                  <div className="face-enroll-actions">
                    <button
                      type="button"
                      className="face-enroll-button face-enroll-button--ghost"
                      onClick={handleToggleCamera}
                    >
                      {cameraOn ? "Выключить камеру" : "Включить камеру"}
                    </button>

                    <button
                      type="button"
                      className="face-enroll-button face-enroll-button--secondary"
                      onClick={handleCapture}
                      disabled={!selectedEmployee || !isCameraReady}
                    >
                      {capturedPhotoUrl ? "Переснять" : "Сделать фото"}
                    </button>

                    <button
                      type="button"
                      className="face-enroll-button face-enroll-button--primary"
                      onClick={handleSaveFace}
                      disabled={
                        !selectedEmployee ||
                        !capturedPhotoUrl ||
                        isSubmitting
                      }
                    >
                      {isSubmitting ? "Сохраняем..." : "Сохранить лицо"}
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    // </Layout>
  );
};

export default FaceEnroll;
